import { ActivitySpinnerWidget, ButtonType, ButtonWidget, TextAreaWidget, TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CloseAndDismiss } from '~src/commonInfra/dialogs/ReactModalService';
import { DialogFieldParentStyle, DialogTextFieldStyle } from '~src/commonInfra/dialogs/style/DialogStyles';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { GetSiteEncryptionKeyPolicyRequest, GetSiteEncryptionKeyPolicyResult } from '../serverApi/requests/GetSiteEncryptionSingleKeyPolicyRequest';
import { getSiteEncryptionKeyPolicy } from '../services/ExtractEncryptionKeyService';
import { getFormattedKeyPolicy } from '../utils/ExtractEncrpytionUtils';

export interface KMSKeyPolicyViewProps {
    closeDialog: CloseAndDismiss;
    externalKeyId: string;
    isAfterKeyGeneration: boolean;
}

export interface KMSKeyPolicyViewState {
    isFetchingKeyPolicy: boolean;
    keyPolicy: string;
}

export class KMSKeyPolicyView extends React.PureComponent<KMSKeyPolicyViewProps, KMSKeyPolicyViewState> {

    constructor(props: KMSKeyPolicyViewProps) {
        super(props);
        this.state = {
            isFetchingKeyPolicy: true,
            keyPolicy: ''
        };
    }

    private copyToClipboard(text: string) {
        navigator.clipboard.writeText(text);
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.extractEncrpytion_KMSKeyPolicyDialog_Copy_Toast() });
    }

    public componentDidMount() {
        this.fetchKeyPolicy();
    }

    public render(): JSX.Element {
        return <>
            <div style={DialogFieldParentStyle}>
                <span>{Messages.extractEncrpytion_KMSKeyPolicyDialog_Copy_Description()}
                    <TextLinkWidget
                        testId={'kms-key-policy-learn-more'}
                        target={'_blank'}
                        href={HelpService.getHelpUrlByCategory('online-kms-external-policy')}
                        className={learnMoreStyle}>
                        {Messages.learnMore()}
                    </TextLinkWidget>
                </span>
            </div>
            <div style={{...DialogFieldParentStyle, display: 'flex', justifyContent: 'center'}}>
                {this.state.isFetchingKeyPolicy ? <ActivitySpinnerWidget /> :
                <TextAreaWidget
                    label={Messages.extractEncryption_KMSKeyPolicyDialog_Title()}
                    style={{height: '250px'}}
                    value={getFormattedKeyPolicy(this.state.keyPolicy)}
                    readOnly={true}
                    className={DialogTextFieldStyle}
                    testId='key-policy-text'
                    />
                }
            </div>
            <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <ButtonWidget
                        buttonType={ButtonType.Outline}
                        testId='copy-button'
                        onClick={() => this.copyToClipboard(getFormattedKeyPolicy(this.state.keyPolicy))}
                        children={Messages.extractEncrpytion_generateKMSKeyDialog_Step2_Copy_Key_Policy()}
                        />
                    <ButtonWidget
                        buttonType={ButtonType.Primary}
                        testId='done-button'
                        onClick={() => this.handleCloseDialog()}
                        children={Messages.done()}
                        />
                </div>
            </div>
        </>;
    }

    private fetchKeyPolicy() {
        this.setState({isFetchingKeyPolicy: true}, () => {
            getSiteEncryptionKeyPolicy(new GetSiteEncryptionKeyPolicyRequest({externalKeyId: this.props.externalKeyId}))
            .then((response: GetSiteEncryptionKeyPolicyResult) => {
                this.setState({keyPolicy: response.policy});
            }).catch(() => {
                ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncrpytion_KMSKeyPolicyDialog_FailedFetch_Toast() });
            }).finally(() => {
                this.setState({isFetchingKeyPolicy: false});
            });
        });
    }

    private handleCloseDialog() {
        if(this.props.isAfterKeyGeneration) {
            ToasterService.instance.pop({ type: ToastType.Info, text: Messages.extractEncryption_KMSKeyPendingToast() });
        }
        this.props.closeDialog.close();
    }
}